import { StorageUnit } from '../types/storageUnits';
import { LevelModel } from '../types/protocols';
import { naturalCompare } from './naturalSort';
import { levelDisplayName } from './levelDisplayName';
import { RENTED_COLOR, VACANT_COLOR } from './leaseStatusColors';

/**
 * Pure helpers for storage units (förråd). Units are flat under the building
 * and bound to a level; the level's current blueprint is the drawing.
 */

/** One icon for every storage unit type (colour PNG in public/, drawn as it is); the type is shown as text. */
export const STORAGE_UNIT_ICON = '/Images/UI/storage-unit.png';

export const storageUnitDisplayName = (unit: Pick<StorageUnit, 'number'>): string => {
    const trimmed = (unit.number || '').trim();
    return trimmed.length > 0 ? `Förråd ${trimmed}` : 'Förråd utan nummer';
};

/** Units in natural number order (2 < 10, K3 < K12), key as tie-breaker. */
export const sortedStorageUnits = (units: { [unitKey: string]: StorageUnit } | StorageUnit[] | undefined): StorageUnit[] => {
    const list = Array.isArray(units) ? units : Object.values(units || {});
    return [...list].sort((a, b) => {
        const byNumber = naturalCompare(a.number || '', b.number || '');
        return byNumber !== 0 ? byNumber : a.key.localeCompare(b.key);
    });
};

export const unitsOnLevel = (units: StorageUnit[], levelKey: string): StorageUnit[] =>
    units.filter(unit => unit.levelKey === levelKey);

/** Rented state is the explicit `currentLeaseKey` written by leasesApi; no date logic here. */
export const isUnitRented = (unit: Pick<StorageUnit, 'currentLeaseKey'>): boolean =>
    typeof unit.currentLeaseKey === 'string' && unit.currentLeaseKey.length > 0;

/** Marker colour: rented (`currentLeaseKey` set, so an upcoming-only lease too) is grey, else green. */
export const storageUnitStatusColor = (unit: Pick<StorageUnit, 'currentLeaseKey'>): string =>
    isUnitRented(unit) ? RENTED_COLOR : VACANT_COLOR;

export const vacantUnitCount = (units: Pick<StorageUnit, 'currentLeaseKey'>[]): number =>
    units.filter(unit => !isUnitRented(unit)).length;

/** Sum of `area` over the units, ignoring units without one. */
export const totalUnitArea = (units: Pick<StorageUnit, 'area'>[]): number =>
    units.reduce((sum, unit) => sum + (typeof unit.area === 'number' && Number.isFinite(unit.area) ? unit.area : 0), 0);

/** Levels a unit can belong to: every level, highest number first. */
export const storageLevelsDescending = (levels: { [key: string]: LevelModel } | undefined): LevelModel[] =>
    Object.values(levels || {})
        .sort((a, b) => b.number - a.number || a.key.localeCompare(b.key));

const hasCurrentBlueprint = (level: LevelModel): boolean =>
    !!level.currentBlueprintKey && !!level.blueprints?.[level.currentBlueprintKey];

const lowest = (levels: LevelModel[]): LevelModel | undefined =>
    [...levels].sort((a, b) => a.number - b.number || a.key.localeCompare(b.key))[0];

/**
 * Default level for the Förråd screen (plan D5): the lowest level that has a
 * drawing and at least one unit; else the lowest level with a drawing; else
 * the lowest level. Basements are the common case, which is why this
 * deliberately inverts the rooms tab's "first level >= 0" rule.
 */
export const defaultStorageLevelKey = (
    levels: { [key: string]: LevelModel } | LevelModel[] | undefined,
    units: Pick<StorageUnit, 'levelKey'>[]
): string | null => {
    const candidates = Array.isArray(levels) ? levels : Object.values(levels || {});
    if (candidates.length === 0) return null;
    const withDrawing = candidates.filter(hasCurrentBlueprint);
    const levelKeysWithUnits = new Set(units.map(unit => unit.levelKey));
    return (
        lowest(withDrawing.filter(level => levelKeysWithUnits.has(level.key))) ??
        lowest(withDrawing) ??
        lowest(candidates)
    )?.key ?? null;
};

/**
 * Display name of the level on which another unit already uses `number`, or
 * null. Same number on another level is fine (plan D15: soft warning only).
 */
export const duplicateUnitLevelName = (
    units: Pick<StorageUnit, 'key' | 'number' | 'levelKey'>[],
    levels: { [key: string]: LevelModel } | undefined,
    levelKey: string,
    number: string,
    excludingKey?: string
): string | null => {
    const wanted = (number || '').trim().toUpperCase();
    if (!wanted) return null;
    const clash = units.find(unit =>
        unit.key !== excludingKey &&
        unit.levelKey === levelKey &&
        (unit.number || '').trim().toUpperCase() === wanted
    );
    if (!clash) return null;
    const level = levels?.[levelKey];
    return level ? levelDisplayName(level) : 'samma våning';
};
